import { SignaturePiece } from '../signatures/SignaturesPage.js';
import { useState, type JSX } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Select, Tabs, Dialog, Input, Textarea } from '@artosera/ui';
import { GENRES_CONTRAT, type GenreContrat, type Langue } from '@artosera/shared';
import {
  useContrats,
  useSignerContrat,
  useAnnulerContrat,
  useContratVersCoffre,
  useOuvrirContratPdf,
  type ContratPresente,
} from '../../api/contrats.js';
import { useArtiste } from '../../api/artistes.js';
import { useContact } from '../../api/contacts.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NouveauContratDialog } from './NouveauContratDialog.js';
import { EditeurModeleContrat } from './EditeurModeleContrat.js';
import { Field } from '../Field.js';
import { TON_ETAT, dateContrat, erreurContrat } from './contratUi.js';
import styles from './ContratsPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';
export function ContratsPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [params, setParams] = useSearchParams();
  const [genre, setGenre] = useState<GenreContrat | undefined>();
  const [modele, setModele] = useState<GenreContrat>('nda');
  const [langue, setLangue] = useState<Langue>('fr');
  const [page, setPage] = useState(1);
  const liste = useContrats(galerieSlug, { ...(genre ? { genre } : {}), page });
  const peutEcrire = galerieALaction(galerie, 'contracts:write');
  const peutModeles = galerieALaction(galerie, 'contracts:templates');
  const label = (s: string) => t(`regie:contrats.${s}`);
  const genres = GENRES_CONTRAT.map((value) => ({ value, label: label(`genres.${value}`) }));
  const contenu = (
    <div className={styles.form}>
      <div className={styles.filtres} data-ui="filters">
        <Select
          aria-label={label('filtreGenre')}
          value={genre ?? 'tous'}
          options={[{ value: 'tous', label: label('tous') }, ...genres]}
          onValueChange={(v) => {
            setGenre(v === 'tous' ? undefined : (v as GenreContrat));
            setPage(1);
          }}
        />
      </div>
      {liste.isPending && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && <p role="alert">{erreurContrat(liste.error, t('commun:erreurReseau'))}</p>}
      {liste.data?.contrats.length === 0 && <p>{label('aucun')}</p>}
      <div className={styles.liste} data-ui="object-list">
        {liste.data?.contrats.map((c) => (
          <Ligne
            key={c.id}
            contrat={c}
            galerieSlug={galerieSlug}
            peutEcrire={peutEcrire}
            avecCoffre={
              peutEcrire &&
              galerieEcritModule(galerie, 'coffre') &&
              galerieALaction(galerie, 'documents:write')
            }
            avecSignature={galerieEcritModule(galerie, 'signature-electronique')}
            lireArtistes={galerieALaction(galerie, 'artists:read')}
            lireContacts={galerieALaction(galerie, 'contacts:read')}
            langue={i18n.language}
          />
        ))}
      </div>
      {liste.data && liste.data.pagination.pages > 1 && (
        <nav className={styles.actions} aria-label={label('pagination')}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
            {label('precedent')}
          </Button>
          <span>
            {page}/{liste.data.pagination.pages}
          </span>
          <Button disabled={page >= liste.data.pagination.pages} onClick={() => setPage(page + 1)}>
            {label('suivant')}
          </Button>
        </nav>
      )}
    </div>
  );
  return (
    <section className={styles.page} data-ui="page">
      <header className={styles.entete} data-ui="page-heading">
        <div>
          <h1>{label('titre')}</h1>
          <p>{label('sousTitre')}</p>
        </div>
        <Verrouillable action="contracts:write" ouvert={peutEcrire}>
          <Button onClick={() => setParams({ nouveau: '1' })}>{label('nouveau')}</Button>
        </Verrouillable>
      </header>
      <Tabs
        value={peutModeles && params.get('onglet') === 'modeles' ? 'modeles' : 'contrats'}
        onValueChange={(v) => setParams({ onglet: v })}
        tabs={[
          { value: 'contrats', label: label('onglets.contrats'), content: contenu },
          ...(peutModeles
            ? [
                {
                  value: 'modeles',
                  label: label('onglets.modeles'),
                  content: (
                    <div className={styles.form}>
                      <div className={styles.filtres}>
                        <Select
                          aria-label={label('modele.choix')}
                          value={modele}
                          options={genres}
                          onValueChange={(v) => setModele(v as GenreContrat)}
                        />
                        <Select
                          aria-label={label('langue')}
                          value={langue}
                          onValueChange={(v) => setLangue(v as Langue)}
                          options={[
                            { value: 'fr', label: 'Français' },
                            { value: 'en', label: 'English' },
                          ]}
                        />
                      </div>
                      <EditeurModeleContrat
                        galerieSlug={galerieSlug}
                        genre={modele}
                        langue={langue}
                      />
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
      {peutEcrire && params.get('nouveau') === '1' && (
        <NouveauContratDialog
          galerieSlug={galerieSlug}
          signataireArtiste={galerieAModule(galerie, 'signature-electronique')}
          ouvert
          onOuvertChange={(v) => {
            if (!v) setParams({ onglet: 'contrats' });
          }}
          initial={{
            ...(GENRES_CONTRAT.includes(params.get('genre') as GenreContrat)
              ? { genre: params.get('genre') as GenreContrat }
              : {}),
            ...(params.get('artisteId') ? { artisteId: params.get('artisteId')! } : {}),
            ...(params.get('contactId') ? { contactId: params.get('contactId')! } : {}),
          }}
        />
      )}
    </section>
  );
}
function Ligne({
  contrat: c,
  galerieSlug,
  peutEcrire,
  avecCoffre,
  avecSignature,
  lireArtistes,
  lireContacts,
  langue,
}: {
  contrat: ContratPresente;
  galerieSlug: string;
  peutEcrire: boolean;
  avecCoffre: boolean;
  avecSignature: boolean;
  lireArtistes: boolean;
  lireContacts: boolean;
  langue: string;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const label = (s: string) => t(`regie:contrats.${s}`);
  const artiste = useArtiste(galerieSlug, lireArtistes ? c.artisteId : null);
  const contact = useContact(galerieSlug, lireContacts ? c.contactId : null);
  const signe = useSignerContrat(galerieSlug);
  const annuler = useAnnulerContrat(galerieSlug);
  const coffre = useContratVersCoffre(galerieSlug);
  const pdf = useOuvrirContratPdf(galerieSlug);
  const [geste, setGeste] = useState<'signer' | 'annuler' | null>(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  async function agir(fn: () => Promise<unknown>) {
    setErreur('');
    try {
      await fn();
      setGeste(null);
    } catch (e) {
      setErreur(erreurContrat(e, t('commun:erreurReseau')));
    }
  }
  const partie = artiste.data?.nom ?? contact.data?.contact.nomAffiche;
  return (
    <article className={styles.ligne} data-ui="object-row">
      <div className={styles.corps}>
        <div className={styles.reference}>
          <span className={styles.numero}>{c.numero}</span>
          <Badge tone={TON_ETAT[c.etat]}>{label(`etats.${c.etat}`)}</Badge>
        </div>
        <h2 className={styles.titreContrat}>{c.titre}</h2>
        <p className={styles.partie}>
          {partie || '—'} · {dateContrat(c.emisLe, langue)}
        </p>
        {avecSignature && <SignaturePiece source={{ type: 'contrat_modele', id: c.id }} />}
        {c.mentionValidation && <p className={styles.aValider}>{label('aValider')}</p>}
        {erreur && <p role="alert">{erreur}</p>}
      </div>
      <div className={styles.actions}>
        <Button
          variant="secondary"
          size="sm"
          loading={pdf.isPending}
          onClick={() => void agir(async () => ouvrirUrlSignee((await pdf.mutateAsync(c.id)).url))}
        >
          {label('pdf')}
        </Button>
        {peutEcrire && c.etat === 'genere' && (
          <Button size="sm" onClick={() => setGeste('signer')}>
            {label('signer')}
          </Button>
        )}
        {peutEcrire && c.etat === 'genere' && (
          <Button variant="danger" size="sm" onClick={() => setGeste('annuler')}>
            {label('annuler')}
          </Button>
        )}
        {avecCoffre && c.etat !== 'annule' && (
          <Button
            variant="secondary"
            size="sm"
            disabled={c.documentId !== null}
            loading={coffre.isPending}
            onClick={() => void agir(() => coffre.mutateAsync(c.id))}
          >
            {label('rangerAuCoffre')}
          </Button>
        )}
      </div>
      <Dialog
        open={geste !== null}
        onOpenChange={(v) => {
          if (!v) setGeste(null);
        }}
        title={label(geste ?? 'signer')}
        closeLabel={t('commun:actions.fermer')}
      >
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void agir(() =>
              geste === 'signer'
                ? signe.mutateAsync({ id: c.id, signeLe: date })
                : annuler.mutateAsync({ id: c.id, motif }),
            );
          }}
        >
          {geste === 'signer' ? (
            <Field htmlFor={`signature-${c.id}`} label={label('dateSignature')}>
              <Input
                id={`signature-${c.id}`}
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>
          ) : (
            <Field htmlFor={`motif-${c.id}`} label={label('motif')}>
              <Textarea
                id={`motif-${c.id}`}
                required
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
              />
            </Field>
          )}
          {erreur && <p role="alert">{erreur}</p>}
          <Button
            type="submit"
            disabled={geste === 'annuler' && !motif.trim()}
            loading={signe.isPending || annuler.isPending}
          >
            {label(geste ?? 'signer')}
          </Button>
        </form>
      </Dialog>
    </article>
  );
}
